/**
 * ChatMessages Component
 * Container for displaying chat messages list
 */

import React from "react";
import { Box, Typography, Paper, CircularProgress, useTheme, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatTime } from "./utils/messageUtils";
import { shouldDisplayMessage } from "./utils/messageUtils";
import ChatMessageContent from "./ChatMessage";

const ChatMessages = ({
        messages,
        streamingText,
        isStreaming,
        isLoading,
        messagesEndRef,
        mainColor,
        onToolClick,
        toolLoadingId,
        isDisabled,
}) => {
        const { t } = useTranslation();
        const theme = useTheme();

        const displayableMessages = messages.filter((msg) => shouldDisplayMessage(msg, messages));

        return (
                <Box
                        sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                backgroundColor: alpha(theme.palette.background.paper, 0.4),
                                p: 2,
                                overflow: "hidden",
                        }}
                >
                        <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                                {isLoading && messages.length === 0 ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                                <CircularProgress size={24} />
                                        </Box>
                                ) : displayableMessages.length === 0 ? (
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                                <Typography variant="body2" color="text.secondary" align="center">
                                                        {t("ai-chat-loading") || "Loading AI assistant..."}
                                                </Typography>
                                        </Box>
                                ) : (
                                        <>
                                                {displayableMessages.map((msg) => (
                                                        <Box
                                                                key={msg.id}
                                                                sx={{
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: msg.sender === "user" ? "flex-end" : "flex-start",
                                                                        mb: 2,
                                                                }}
                                                        >
                                                                <Paper
                                                                        elevation={1}
                                                                        sx={{
                                                                                p: 1.5,
                                                                                maxWidth: "80%",
                                                                                borderRadius:
                                                                                        msg.sender === "user"
                                                                                                ? "20px 20px 4px 20px"
                                                                                                : "20px 20px 20px 4px",
                                                                                bgcolor:
                                                                                        msg.sender === "user"
                                                                                                ? alpha(mainColor, 0.1)
                                                                                                : theme.palette.grey[100],
                                                                        }}
                                                                >
                                                                        {msg.sender === "ai" ? (
                                                                                <ChatMessageContent
                                                                                        message={msg}
                                                                                        toolLoadingId={toolLoadingId}
                                                                                        isDisabled={isDisabled}
                                                                                        onToolClick={onToolClick}
                                                                                />
                                                                        ) : (
                                                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                                                        {msg.content}
                                                                                </Typography>
                                                                        )}
                                                                </Paper>
                                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                                                                        {formatTime(msg.timestamp)}
                                                                </Typography>
                                                        </Box>
                                                ))}

                                                {isStreaming && (
                                                        <Box
                                                                sx={{
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: "flex-start",
                                                                        mb: 2,
                                                                }}
                                                        >
                                                                <Paper
                                                                        elevation={1}
                                                                        sx={{
                                                                                p: 1.5,
                                                                                maxWidth: "80%",
                                                                                borderRadius: "20px 20px 20px 4px",
                                                                                bgcolor: theme.palette.grey[100],
                                                                        }}
                                                                >
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                                <CircularProgress size={16} />
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                        {t("ai-chat-processing") || "Processing your request..."}
                                                                                </Typography>
                                                                        </Box>
                                                                </Paper>
                                                        </Box>
                                                )}

                                                <div ref={messagesEndRef} />
                                        </>
                                )}
                        </Box>
                </Box>
        );
};

export default ChatMessages;

